import { useState, useEffect, type JSX } from 'react'
import { PushPromptService } from './push-prompt.service';
import { usePushNotifications } from './usePushNotifications';

export function PushPrompt(): JSX.Element | null {
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [debugInfo, setDebugInfo] = useState<string>('');
    const { subscribe, isSubscribed, permission } = usePushNotifications();

    useEffect(() => {
        console.log('[PushPrompt] Initializing...');
        console.log('[PushPrompt] Current permission:', permission);
        console.log('[PushPrompt] Should show:', PushPromptService.shouldShow());
        console.log('[PushPrompt] isSubscribed:', isSubscribed);

        const timer = setTimeout(() => {
            const shouldShow = PushPromptService.shouldShow();
            console.log('[PushPrompt] Timer fired, shouldShow:', shouldShow);
            
            if (shouldShow) {
                setIsVisible(true);
                setIsAnimating(true);
            }
        }, 3000)

        return () => clearTimeout(timer);
    }, [permission, isSubscribed])

    const handleAllow = async (): Promise<void> => {
        console.log('[PushPrompt] Allow clicked');
        console.log('[PushPrompt] Current permission before request:', Notification.permission);
        
        try {
            // Сначала запрашиваем разрешение напрямую
            const permResult = await Notification.requestPermission();
            console.log('[PushPrompt] Permission request result:', permResult);
            
            if (permResult === 'granted') {
                console.log('[PushPrompt] Permission granted, calling subscribe...');
                await subscribe();
                PushPromptService.setState('granted');
                handleDismiss();
            } else {
                console.log('[PushPrompt] Permission denied:', permResult);
                PushPromptService.setState('denied');
                setDebugInfo(`Разрешение отклонено: ${permResult}`);
                handleDismiss();
            }
        } catch (err) {
            console.error('[PushPrompt] Error in handleAllow:', err);
            setDebugInfo(`Ошибка: ${err instanceof Error ? err.message : String(err)}`);
        }
    }

    const handleDeny = (): void => {
        console.log('[PushPrompt] Deny clicked');
        PushPromptService.setState('denied');
        handleDismiss();
    }

    const handleDismiss = (): void => {
        console.log('[PushPrompt] Dismissing');
        setIsAnimating(false);
        setTimeout(() => setIsVisible(false), 300);
    }

    useEffect(() => {
        if (isSubscribed) {
            console.log('[PushPrompt] User subscribed, hiding prompt');
            handleDismiss();
        }
    }, [isSubscribed])

    if (!isVisible) {
        console.log('[PushPrompt] Not rendering (not visible)');
        return null;
    }

    console.log('[PushPrompt] Rendering prompt')

    return (
        <>
            <div
                className={`
                    fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96
                    bg-gradient-to-r from-blue-600 to-blue-700
                    text-white p-4 rounded-lg shadow-2xl z-50
                    transform transition-all duration-300 ease-out
                    ${isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
                `}
            >
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                            />
                        </svg>
                    </div>

                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm md:text-base">
                            Включить уведомления?
                        </h3>
                        <p className="text-blue-100 text-xs md:text-sm mt-1">
                            Получайте мгновенные уведомления о падении сайтов и критических инцидентах
                        </p>
                        
                        {debugInfo && (
                            <p className="text-yellow-200 text-xs mt-2 bg-red-900/50 p-1 rounded">
                                {debugInfo}
                            </p>
                        )}

                        <div className="flex gap-2 mt-3">
                            <button
                                onClick={handleAllow}
                                className="flex-1 bg-white text-blue-600 px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-50 transition"
                            >
                                Включить
                            </button>
                            <button
                                onClick={handleDeny}
                                className="px-3 py-1.5 text-blue-100 text-sm hover:text-white transition"
                            >
                                Нет
                            </button>
                        </div>
                    </div>

                    <button
                        onClick={handleDismiss}
                        className="flex-shrink-0 text-blue-200 hover:text-white transition"
                        title="Закрыть"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="fixed top-4 right-4 bg-black/80 text-green-400 p-2 rounded text-xs font-mono z-50 max-w-xs">
                <div>Permission: {permission}</div>
                <div>Subscribed: {isSubscribed ? 'yes' : 'no'}</div>
                <div>
                    Should show:
                    {PushPromptService.shouldShow() ? 'yes' : 'no'}
                </div>
                <div>Visible: {isVisible ? 'yes' : 'no'}</div>
            </div>
        </>
    )
}